import React, { useCallback, useState } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  StyleSheet,
  Platform,
  Linking,
  Alert,
  RefreshControl,
  TouchableOpacity,
} from 'react-native';
import { ScreenContainer } from '../../../layout/ScreenContainer';
import { LineTitle } from '../../../molecules/LineTitle';
import { MediumText } from '../../../atoms/MediumText';
import Button from '../../../atoms/Button';
import { Spinner } from '../../../organisms/Spinner';
import {
  getDDRHistory,
  getDDRPresignedUrl,
  DDRSubmission,
} from '../../../../services/ddrService';
import { appConfig } from '../../../../config/config';

export const DDRSubmissionsScreen = (): React.ReactElement => {
  const [submissions, setSubmissions] = useState<DDRSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  const loadDDRHistory = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const response = await getDDRHistory();

      if (response && Array.isArray(response)) {
        setSubmissions(response);
      } else {
        Alert.alert('Error', 'Failed to load DDR history');
      }
    } catch (error) {
      console.error('Failed to load DDR history:', error);
      Alert.alert('Error', 'Failed to load DDR history. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const handleDownload = useCallback(async (submissionId: string) => {
    try {
      setDownloadingIds((prev) => new Set(prev).add(submissionId));

      const response = await getDDRPresignedUrl(submissionId);

      if (response && response.presignedUrl) {
        if (Platform.OS === 'web') {
          // For web, open in new tab using window.open if available
          if (typeof window !== 'undefined' && window.open) {
            window.open(response.presignedUrl, '_blank');
          } else {
            // Fallback for web if window.open is not available
            await Linking.openURL(response.presignedUrl);
          }
        } else {
          // For mobile, use Linking
          const supported = await Linking.canOpenURL(response.presignedUrl);
          if (supported) {
            await Linking.openURL(response.presignedUrl);
          } else {
            Alert.alert('Error', 'Unable to open document');
          }
        }
      } else {
        Alert.alert('Error', 'Failed to generate download link');
      }
    } catch (error) {
      console.error('Failed to download DDR:', error);
      Alert.alert('Error', 'Failed to download document. Please try again.');
    } finally {
      setDownloadingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(submissionId);
        return newSet;
      });
    }
  }, []);

  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return `${dateStr}\n${timeStr}`;
  }, []);

  useFocusEffect(
    useCallback(() => {
      loadDDRHistory();
    }, [loadDDRHistory]),
  );

  if (loading && submissions.length === 0) {
    return (
      <ScreenContainer>
        <LineTitle title="DDR Submissions" />
        <View style={styles.centerContainer}>
          <Spinner />
        </View>
      </ScreenContainer>
    );
  }

  return (
    <ScrollView
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={() => loadDDRHistory(true)}
          tintColor={appConfig.primaryColour}
        />
      }
    >
      <ScreenContainer>
        <LineTitle title="DDR Submissions" />

        <MediumText style={styles.description}>
          View and download your Direct Debit Request documents
        </MediumText>

        {submissions.length === 0 ? (
          <View style={styles.emptyContainer}>
            <MediumText style={styles.emptyText}>
              No DDR documents found
            </MediumText>
          </View>
        ) : (
          <View style={styles.tableContainer}>
            {/* Table Header */}
            <View style={styles.headerRow}>
              <View style={styles.dateColumn}>
                <MediumText style={styles.headerText}>Date & Time</MediumText>
              </View>
              <View style={styles.nameColumn}>
                <MediumText style={styles.headerText}>Name</MediumText>
              </View>
              <View style={styles.actionColumn}>
                <MediumText style={styles.headerText}>Download</MediumText>
              </View>
            </View>

            {/* Table Rows */}
            {submissions.map((submission, index) => (
              <View
                key={submission._id}
                style={[
                  styles.dataRow,
                  index % 2 === 0 ? styles.evenRow : styles.oddRow,
                ]}
              >
                <View style={styles.dateColumn}>
                  <MediumText style={styles.dataText}>
                    {formatDate(submission._createdDate)}
                  </MediumText>
                </View>
                <View style={styles.nameColumn}>
                  <MediumText style={styles.dataText} numberOfLines={2}>
                    {submission.name}
                  </MediumText>
                </View>
                <View style={styles.actionColumn}>
                  <TouchableOpacity
                    style={styles.downloadButton}
                    onPress={() => handleDownload(submission._id)}
                    disabled={downloadingIds.has(submission._id)}
                  >
                    <MediumText
                      style={[
                        styles.downloadIcon,
                        downloadingIds.has(submission._id) &&
                          styles.downloadIconDisabled,
                      ]}
                    >
                      {downloadingIds.has(submission._id) ? '⏳' : '⬇️'}
                    </MediumText>
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </View>
        )}
      </ScreenContainer>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  description: {
    marginBottom: 20,
    color: '#666',
    textAlign: 'center',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  emptyText: {
    color: '#666',
    fontSize: 16,
  },
  tableContainer: {
    backgroundColor: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  headerRow: {
    flexDirection: 'row',
    backgroundColor: appConfig.primaryColour,
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  dataRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  evenRow: {
    backgroundColor: '#fff',
  },
  oddRow: {
    backgroundColor: '#f9f9f9',
  },
  dateColumn: {
    flex: 1,
    justifyContent: 'center',
  },
  nameColumn: {
    flex: 2,
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  actionColumn: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 14,
  },
  dataText: {
    color: '#333',
    fontSize: 12,
  },
  downloadButton: {
    padding: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  downloadIcon: {
    fontSize: 18,
    color: appConfig.primaryColour,
  },
  downloadIconDisabled: {
    color: '#ccc',
  },
});
